import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import {
  SalesOrder,
  Quote,
  Panel,
  QuoteLineItem,
  SalesOrderLineItem,
  Load,
  Product,
  ProductionOrder,
  ProductionScheduleData,
  DispatchAssignmentData,
} from "./types";
import {
  SalesOrderWorkflowData,
  Account,
  User,
  Job,
} from "../../types/SalesOrderTypes";
import { ViewStoreErrorHandler } from "@intrasync/stores/utils";

interface TableStats {
  tableNumber: number;
  tablePanelCount: number;
  gridRow: number;
  gridPosition: number;
  gridSize: number;
}

// Mock data for products
export const mockProducts = [
  {
    id: 101,
    productCode: "PWP-10FT",
    name: "10ft Precast Wall Panel",
    description:
      "Standard 10-foot precast concrete wall panel for construction projects",
    productType: "precast",
    basePrice: 1500,
    weight: 2800,
    dimensions: "10' x 8' x 6\"",
    material: "Reinforced Concrete",
    active: true,
  },
  {
    id: 102,
    productCode: "IWP-12FT",
    name: "12ft Insulated Wall Panel",
    description: "Insulated 12-foot wall panel with thermal barrier",
    productType: "precast",
    basePrice: 2500,
    weight: 3200,
    dimensions: "12' x 8' x 8\"",
    material: "Insulated Concrete",
    active: true,
  },
  {
    id: 103,
    productCode: "SBP-8FT",
    name: "8ft Sound Barrier Panel",
    description: "Acoustic sound barrier panel for noise reduction",
    productType: "barrier",
    basePrice: 1800,
    weight: 2200,
    dimensions: "8' x 10' x 4\"",
    material: "Dense Concrete",
    active: true,
  },
  {
    id: 104,
    productCode: "DFP-6FT",
    name: "6ft Decorative Fence Panel",
    description: "Decorative concrete fence panel with architectural finish",
    productType: "precast",
    basePrice: 1200,
    weight: 1800,
    dimensions: "6' x 6' x 4\"",
    material: "Decorative Concrete",
    active: true,
  },
  {
    id: 105,
    productCode: "SSB-20FT",
    name: "20ft Structural Support Beam",
    description: "Heavy-duty structural concrete beam for large spans",
    productType: "precast",
    basePrice: 5000,
    weight: 8500,
    dimensions: "20' x 2' x 2'",
    material: "High-Strength Concrete",
    active: true,
  },
];

export default class SalesOrderViewStore {
  rootStore: RootStore;
  errorHandler: ViewStoreErrorHandler;

  // Observables - Step Management
  currentStep = 1;
  isLoading = false;

  // Observables - Entity IDs (following ServiceStore pattern)
  activeQuoteId: number | undefined = undefined;
  activeSalesOrderId: number | undefined = undefined;
  selectedPanelIds: number[] = [];
  assignedLoadId: string | number | null = undefined;
  createdPanelIds: number[] = []; // Track panels created in current workflow
  selectedProjectId: number | undefined = undefined; // Selected job/project ID
  selectedAccountId: number | undefined = undefined; // Selected account ID

  // Observables - UI State
  showPanelDetails = false;
  scheduleDate: string | null = undefined;
  panelTableAssignments: { [key: string]: string } = {}; // Track table assignments for panels

  // FIXED: Removed mock data storage - entities now accessed via ServiceStores
  // All API entities are accessed through computed properties from ServiceStores

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false, errorHandler: false });
    this.rootStore = rootStore;

    // Initialize standardized error handling
    this.errorHandler = new ViewStoreErrorHandler(
      rootStore,
      "SalesOrderViewStore",
      {
        enableErrorNotifications: true,
        enableReactionBatching: true,
        debugMode: false,
      }
    );
  }

  // Computed Values - Access entities from ServiceStores
  get activeQuote(): Quote | null {
    if (!this.activeQuoteId) return null;
    const store = this.rootStore.quotesStore as ServiceStore;
    return (
      (store?.entities?.find(
        (quote: any) =>
          (quote as unknown as Quote).quoteId === this.activeQuoteId
      ) as unknown as Quote) || null
    );
  }

  get activeSalesOrder(): SalesOrder | null {
    if (!this.activeSalesOrderId) return null;
    const store = this.rootStore.salesOrdersStore as ServiceStore;
    return (
      (store?.entities?.find(
        (so: any) =>
          (so as unknown as SalesOrder).salesOrderId === this.activeSalesOrderId
      ) as unknown as SalesOrder) || null
    );
  }

  get quoteLineItems(): QuoteLineItem[] {
    if (!this.activeQuoteId) return [];
    const store = this.rootStore.quoteLineItemsStore as ServiceStore;
    return (
      store?.entities
        ?.filter(
          (item: any) =>
            (item as unknown as QuoteLineItem).quoteId ===
            this.activeQuoteId
        )
        .map((item: any) => item as unknown as QuoteLineItem) || []
    );
  }

  get salesOrderLineItems(): SalesOrderLineItem[] {
    if (!this.activeSalesOrderId) return [];
    const store = this.rootStore.salesOrderLineItemsStore as ServiceStore;
    return (
      store?.entities
        ?.filter(
          (item: any) =>
            (item as unknown as SalesOrderLineItem).salesOrderId ===
            this.activeSalesOrderId
        )
        .map((item: any) => item as unknown as SalesOrderLineItem) || []
    );
  }

  get generatedPanels(): Panel[] {
    if (!this.activeSalesOrderId) return [];
    const store = this.rootStore.panelStore as ServiceStore;
    // Filter panels to only include those created in the current workflow
    return (
      store?.entities
        ?.filter((entity: any) => {
          const panel = entity as unknown as Panel;
          return this.createdPanelIds.includes(panel.id);
        })
        .map((entity: any) => entity as unknown as Panel) || []
    );
  }

  get productionOrders(): ProductionOrder[] {
    if (!this.activeSalesOrderId) return [];
    const store = this.rootStore.productionOrdersStore as ServiceStore;
    return (
      store?.entities
        ?.filter((order: any) =>
          (order as unknown as ProductionOrder).orderNumber?.includes(
            `${this.activeSalesOrder?.soNumber}`
          )
        )
        .map((order: any) => order as unknown as ProductionOrder) || []
    );
  }

  get suppliers(): Account[] {
    const store = this.rootStore.suppliersStore as ServiceStore;
    return store?.entities?.map((entity: any) => entity as unknown as Account) || [];
  }

  get accounts(): Account[] {
    const store = this.rootStore.customersStore as ServiceStore;
    return store?.entities?.map((entity: any) => entity as unknown as Account) || [];
  }

  get jobs(): Job[] {
    const store = this.rootStore.jobStore as ServiceStore;
    return store?.entities?.map((entity: any) => entity as unknown as Job) || [];
  }

  get selectedJob(): Job | null {
    if (!this.selectedProjectId) return null;
    const store = this.rootStore.jobStore as ServiceStore;
    return (
      (store?.entities?.find(
        (job: any) => (job as unknown as Job).id === this.selectedProjectId
      ) as unknown as Job) || null
    );
  }

  // Alias for backward compatibility - maps job data to expected project interface
  get projects(): Array<{
    id: number;
    projectName: string;
    accountId?: number;
  }> {
    const store = this.rootStore.jobStore as ServiceStore;
    return (
      store?.entities?.map((entity: any) => {
        const job = entity as unknown as Job;
        return {
          id: job.id,
          projectName: job.name || "", // Map job.name to projectName
          accountId: job.accountId,
        };
      }) || []
    );
  }

  get users(): User[] {
    // Return mock users if needed
    return [
      {
        id: 1,
        name: "Admin User",
        email: "admin@example.com",
        isActive: true,
        role: "Administrator",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ] as User[];
  }

  get assignedLoad(): Load | null {
    if (!this.assignedLoadId) return null;
    const store = this.rootStore.loadStore as ServiceStore;
    return (
      (store?.entities?.find(
        (load: any) => (load as unknown as Load).id === this.assignedLoadId
      ) as unknown as Load) || null
    );
  }

  get products(): Product[] {
    const store = this.rootStore.productsStore as ServiceStore;
    return store?.entities?.map((entity: any) => entity as unknown as Product) || [];
  }

  get tableList(): TableStats[] {
    const castingMapLayoutStore = this.rootStore
      .castingMapLayoutStore as ServiceStore;
    return (
      (castingMapLayoutStore?.entities
        ?.slice()
        ?.filter(
          (table: any) =>
            (table as { startDate: string; endDate: string }).startDate <=
            this.castingDisplayDate &&
            (((table as { startDate: string; endDate: string }).endDate !==
              "1970-01-01" &&
              (table as { startDate: string; endDate: string }).endDate >=
              this.castingDisplayDate) ||
              (table as { startDate: string; endDate: string }).endDate ===
              "1970-01-01")
        ) as unknown as TableStats[]) || []
    );
  }

  get availableTablesFromList() {
    const uniqueTableNumbers = [
      ...new Set(this.tableList.map((i: any) => String(i.tableNumber))),
    ];
    return uniqueTableNumbers.sort((a, b) => Number(a) - Number(b));
  }

  get castingDisplayDate(): string {
    // Use the schedule date if set, otherwise use today's date
    return this.scheduleDate || new Date().toISOString().split("T")[0];
  }

  get quoteSummary() {
    if (!this.activeQuote) return null;
    return {
      quoteNumber: this.activeQuote.quoteNumber,
      totalAmount: this.activeQuote.totalAmount,
      currency: this.activeQuote.currency,
      status: this.activeQuote.status,
      lineItemCount: this.quoteLineItems.length,
      totalQuantity: this.quoteLineItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      ),
    };
  }

  get salesOrderSummary() {
    if (!this.activeSalesOrder) return null;
    return {
      soNumber: this.activeSalesOrder.soNumber,
      totalAmount: this.activeSalesOrder.totalAmount,
      currency: this.activeSalesOrder.currency,
      status: this.activeSalesOrder.status,
      lineItemCount: this.salesOrderLineItems.length,
      totalQuantity: this.salesOrderLineItems.reduce(
        (sum, item) => sum + item.orderedQuantity,
        0
      ),
    };
  }

  get workflowProgress(): number {
    return ((this.currentStep - 1) / 3) * 100;
  }

  get canProceedToNextStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!this.activeQuote;
      case 2:
        return !!this.activeSalesOrder;
      case 3:
        return this.productionOrders.length > 0;
      case 4:
        return !!this.assignedLoad;
      default:
        return false;
    }
  }

  // Error Handling - Computed Properties (matching ServiceStore pattern)
  get hasError(): boolean {
    return this.errorHandler.hasError;
  }

  get hasLoadError(): boolean {
    return this.errorHandler.hasLoadError;
  }

  get hasCRUDError(): boolean {
    return this.errorHandler.hasCRUDError;
  }

  get hasSubmitError(): boolean {
    return this.errorHandler.hasSubmitError;
  }

  get allErrors() {
    return this.errorHandler.allErrors;
  }

  // Actions - UI State Management
  setCurrentStep = (step: number) => {
    this.currentStep = step;
  };

  setActiveQuote = (id: number | undefined) => {
    this.activeQuoteId = id;
  };

  setActiveSalesOrder = (id: number | undefined) => {
    this.activeSalesOrderId = id;
  };

  setSelectedPanels = (panelIds: number[]) => {
    this.selectedPanelIds = panelIds;
  };

  setSelectedProject = (projectId: number | undefined) => {
    this.selectedProjectId = projectId;
  };

  setSelectedAccount = (accountId: number | undefined) => {
    this.selectedAccountId = accountId;
  };

  setAssignedLoad = (loadId: number | undefined) => {
    this.assignedLoadId = loadId;
  };

  togglePanelDetails = () => {
    this.showPanelDetails = !this.showPanelDetails;
  };

  setScheduleDate = (date: string | null) => {
    this.scheduleDate = date;
  };

  setPanelTableAssignment = (panelId: string, tableNumber: string) => {
    this.panelTableAssignments[panelId] = tableNumber;
  };

  getPanelTableAssignment = (panelId: string): string => {
    return this.panelTableAssignments[panelId] || "";
  };

  resetWorkflow = () => {
    this.currentStep = 1;
    this.activeQuoteId = undefined;
    this.activeSalesOrderId = undefined;
    this.selectedPanelIds = [];
    this.assignedLoadId = undefined;
    this.createdPanelIds = [];
    this.selectedProjectId = undefined;
    this.selectedAccountId = undefined;
    this.showPanelDetails = false;
    this.scheduleDate = undefined;
    this.panelTableAssignments = {};
    this.errorHandler.clearAllErrors();
  };

  reset = () => {
    this.resetWorkflow();
  };

  // Actions - Error Management (using standardized error handler)
  setError = (
    operation: "load" | "create" | "update" | "delete" | "submit" | "general",
    error: string | Error,
    context?: Record<string, unknown>
  ) => {
    this.errorHandler.setError(operation, error, context);
  };

  clearError = (
    operation?: "load" | "create" | "update" | "delete" | "submit" | "general"
  ) => {
    if (operation) {
      this.errorHandler.clearError(operation);
    } else {
      this.errorHandler.clearAllErrors();
    }
  };

  // Mock Actions - Quote and Sales Order Operations
  createQuote = async (data: SalesOrderWorkflowData) => {
    this.isLoading = true;

    console.log("Creating quote with data:", data);

    return this.errorHandler
      .withErrorHandling(
        "create",
        async () => {
          const { lineItems } = data;

          // Create quote with required fields
          const newQuote: Quote = {
            quoteNumber: `Q-${Date.now()}`,
            totalAmount: data.totalAmount,
            currency: data.currency || "USD",
            status: "Draft",
            accountId: data.accountId,
            jobId: data.projectId, // Map projectId to jobId
            quoteDate: new Date(data.quoteDate).toISOString(), // Convert date string to ISO datetime
            salesPersonId: data.salesPersonId,
          };

          // Create quote using ServiceStore
          const store = this.rootStore.quotesStore as ServiceStore;
          const newlyCreatedQuote = await store?.create?.(newQuote).catch((error: any) => {
            console.log("Error creating quote:", error);
            return null;
          });

          if (!newlyCreatedQuote) {
            throw new Error("Failed to create quote");
          }

          const createdQuote = newlyCreatedQuote as unknown as Quote;

          // Create quote line items with required fields
          lineItems.forEach(async (item: any) => {
            const lineItem: QuoteLineItem = {
              quoteId: createdQuote.quoteId as number,
              productId: item.productId,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              totalPrice: item.quantity * item.unitPrice,
              description: item.description,
            };
            // Create line item using ServiceStore
            const lineItemStore = this.rootStore
              .quoteLineItemsStore as ServiceStore;
            await lineItemStore?.create?.(lineItem).catch((error: any) => {
              console.log("Error creating quote line item:", error);
            });
          });

          runInAction(() => {
            this.activeQuoteId = createdQuote.quoteId as number;
            this.currentStep = 2;
          });

          this.rootStore.notificationStore?.addNotification({
            message: `Quote ${createdQuote.quoteNumber} created successfully`,
            options: { variant: "success" },
          });
        },
        { operation: "createQuote", data }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  convertQuoteToSalesOrder = async () => {
    if (!this.activeQuoteId || !this.activeQuote) return;

    this.isLoading = true;
    return this.errorHandler
      .withErrorHandling(
        "create",
        async () => {
          // First create the sales order from the quote
          const newSalesOrder: SalesOrder = {
            soNumber: `SO-${Date.now()}`,
            quoteId: this.activeQuoteId,
            accountId: this.activeQuote.accountId,
            jobId: this.activeQuote.jobId,
            salesPersonId: this.activeQuote.salesPersonId,
            orderDate: new Date().toISOString(),
            totalAmount: this.activeQuote.totalAmount,
            currency: this.activeQuote.currency,
            status: "Approved",
          };

          // Create sales order using ServiceStore
          const soStore = this.rootStore.salesOrdersStore as ServiceStore;
          const createdSO = await soStore?.create?.(newSalesOrder).catch((error: any) => {
            console.log("Error creating sales order:", error);
            return null;
          });

          if (!createdSO) {
            throw new Error("Failed to create sales order");
          }

          const salesOrder = createdSO as unknown as SalesOrder;

          runInAction(() => {
            this.activeSalesOrderId = salesOrder.salesOrderId as number;
          });

          // Convert quote line items to sales order line items
          const lineItems = this.quoteLineItems;
          let panelCount = 1;

          // Get the actual jobNumber from the selected job
          const actualJobNumber = this.selectedJob?.jobNumber || 10;

          for (const lineItem of lineItems) {
            for (let i = 0; i < lineItem.quantity; i++) {
              // Generate random 4-digit control number (1000-9999)
              const randomControlNumber =
                Math.floor(Math.random() * 9000) + 1000;

              // Generate random series identifier (A-Z, 01-99)
              const seriesLetter = String.fromCharCode(
                65 + Math.floor(Math.random() * 26)
              ); // A-Z
              const seriesNumber = String(
                Math.floor(Math.random() * 99) + 1
              ).padStart(2, "0"); // 01-99
              const markSeries = `${seriesLetter}${seriesNumber}`;

              // Generate realistic random panel dimensions and properties
              const panelLength = Math.floor(Math.random() * 16) + 8; // 8-24 feet
              const panelHeight = Math.floor(Math.random() * 4) + 8; // 8-12 feet
              const panelThickness = Math.floor(Math.random() * 4) + 4; // 4-8 inches

              // Calculate realistic values based on dimensions
              const totalSqFt = panelLength * panelHeight + Math.random() * 10; // Add some variation
              const volume = (totalSqFt * (panelThickness / 12)) / 27; // Convert to cubic yards
              const assembledPanelWeight =
                totalSqFt * (120 + Math.random() * 30); // 120-150 lbs per sq ft

              // Generate random production details
              const finishTypes = [
                "Smooth",
                "Textured",
                "Architectural",
                "Sandblasted",
                "Exposed Aggregate",
              ];
              const castingTypes = [
                "Standard",
                "Insulated",
                "Structural",
                "Architectural",
              ];
              const productTypes = ["PWP", "IWP", "SBP", "DFP", "SSB"];

              const panelData: Panel = {
                panelId: `${panelCount.toString().padStart(3, "0")}`,
                controlNumber: randomControlNumber.toString(),
                controlUUID: `${actualJobNumber}${randomControlNumber}`,
                markNumber: `M-${markSeries}-${i + 1}`,
                markUUID: `${actualJobNumber}M-${markSeries}-${i + 1}`,
                jobNumber: actualJobNumber,

                // Panel dimensions and specifications
                panelLength: `${panelLength}'-${Math.floor(
                  Math.random() * 12
                )}"`,
                totalSqFt: Math.round(totalSqFt * 10) / 10, // Round to 1 decimal
                volume: Math.round(volume * 100) / 100, // Round to 2 decimals
                assembledPanelWeight: Math.round(assembledPanelWeight),

                // Production details
                finishType:
                  finishTypes[Math.floor(Math.random() * finishTypes.length)],
                castingType:
                  castingTypes[Math.floor(Math.random() * castingTypes.length)],
                productType:
                  productTypes[Math.floor(Math.random() * productTypes.length)],

                // Manufacturing details
                moldNumber: `M${Math.floor(Math.random() * 99) + 1}`,
                // tableNumber: `${Math.floor(Math.random() * 20) + 1}`,
                fabricationSequence: `${Math.floor(Math.random() * 100) + 1}`,
                installationSequence: `${Math.floor(Math.random() * 100) + 1}`,

                // Mix design details
                mixDesign1: `MIX-${Math.floor(Math.random() * 9) + 1}`,
                mix1SqFt: Math.round(totalSqFt * (0.7 + Math.random() * 0.3)), // 70-100% of total
                mix1Volume:
                  Math.round(volume * (0.7 + Math.random() * 0.3) * 100) / 100,

                // Additional properties
                veneerSqFt: Math.round(totalSqFt * (0.8 + Math.random() * 0.2)),
                netExposedSqFt: Math.round(
                  totalSqFt * (0.6 + Math.random() * 0.3)
                ),
                totalMixCubicYd: Math.round(volume * 100) / 100,
                individualPanelWeight: Math.round(assembledPanelWeight * 0.95),

                // Dates (will be set later in workflow)
                castingDate: undefined,
                createdDate: new Date().toISOString().split("T")[0],
                updatedDate: new Date().toISOString().split("T")[0],
              };

              // Create panel using ServiceStore
              const panelStore = this.rootStore.panelStore as ServiceStore;
              panelStore?.create?.(panelData).then((newPanel: any) => {
                if (newPanel && newPanel.id) {
                  // Track created panel ID for filtering
                  this.createdPanelIds.push(newPanel.id as number);
                  console.log(
                    `Panel ${newPanel.id} created successfully with ID ${newPanel.id}`
                  );
                }
              });

              panelCount++;
            }
          }

          // Simulate async delay
          await new Promise((resolve: any) => setTimeout(resolve, 1500));

          runInAction(() => {
            this.currentStep = 3;
          });

          this.rootStore.notificationStore?.addNotification({
            message: `${panelCount - 1} panels generated successfully`,
            options: { variant: "success" },
          });
        },
        {
          operation: "convertToIndividualProducts",
          activeSalesOrderId: this.activeSalesOrderId,
        }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  updatePanelCastingDate = async (panelId: number, castingDate: string) => {
    this.isLoading = true;

    return this.errorHandler
      .withErrorHandling(
        "update",
        async () => {
          // Update panel using ServiceStore
          const panelStore = this.rootStore.panelStore as ServiceStore;
          const panel = panelStore?.entities?.find(
            (p: any) => (p as unknown as Panel).id === panelId
          ) as unknown as Panel;
          if (panel) {
            await panelStore?.patch?.(panelId, { castingDate });
          }

          runInAction(() => {
            this.isLoading = false;
          });

          this.rootStore.notificationStore?.addNotification({
            message: `Panel ${panelId} casting date updated.`,
            options: { variant: "success" },
          });
        },
        { operation: "updatePanelCastingDate", panelId, castingDate }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  updatePanelTableAssignment = async (panelId: number, tableNumber: string) => {
    this.isLoading = true;

    return this.errorHandler
      .withErrorHandling(
        "update",
        async () => {
          // Update panel using ServiceStore
          const panelStore = this.rootStore.panelStore as ServiceStore;
          const panel = panelStore?.entities?.find(
            (p: any) => (p as unknown as Panel).id === panelId
          ) as unknown as Panel;
          if (panel) {
            await panelStore?.patch?.(panelId, { tableNumber });
          }

          runInAction(() => {
            this.isLoading = false;
          });

          this.rootStore.notificationStore?.addNotification({
            message: `Panel ${panelId} table assignment updated to Table ${tableNumber}.`,
            options: { variant: "success" },
          });
        },
        { operation: "updatePanelTableAssignment", panelId, tableNumber }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  scheduleProduction = async (data: ProductionScheduleData) => {
    if (!this.activeSalesOrderId || this.generatedPanels.length === 0) return;

    this.isLoading = true;
    return this.errorHandler
      .withErrorHandling(
        "create",
        async () => {
          const productionOrderData: ProductionOrder = {
            orderNumber: `PRD-${this.activeSalesOrder?.soNumber}`,
            productDescription: `Production for SO ${this.activeSalesOrder?.soNumber}`,
            productionType: "precastPanels",
            plannedStartDate: new Date(data.plannedStartDate).toISOString(),
            plannedEndDate: new Date(data.plannedEndDate).toISOString(),
            priority:
              (data.priority?.toLowerCase() as
                | "low"
                | "medium"
                | "high"
                | "critical") || "medium",
            assignedTo: data.assignedTo,
            supervisorId: data.supervisorId,
            status: "planned",
            notes: data.notes,
            jobId: this.selectedProjectId || this.activeSalesOrder?.jobId,
            accountId: this.selectedAccountId || this.activeSalesOrder?.accountId,
            quantity: this.generatedPanels.length,
            unitOfMeasure: "panels",
            percentComplete: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          console.log(
            "Scheduling production with data:",
            JSON.stringify(productionOrderData, null, 2)
          );

          // Create production order using ServiceStore
          // const productionOrderStore = this.rootStore
          //   .productionOrdersStore as ServiceStore;
          // await productionOrderStore?.create?.(productionOrderData);

          runInAction(() => {
            this.currentStep = 4;
            this.scheduleDate = data.plannedStartDate;
          });

          this.rootStore.notificationStore?.addNotification?.({
            message: `Production scheduled for ${this.generatedPanels.length} panels`,
            options: { variant: "success", autoHideduration: 4000 },
          });
        },
        { operation: "scheduleProduction", data }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  assignToDispatch = async (data: DispatchAssignmentData) => {
    if (!this.activeSalesOrderId) return;

    this.isLoading = true;
    return this.errorHandler
      .withErrorHandling(
        "create",
        async () => {
          const loadData: Load = {
            id: Date.now(),
            loadNumber: `LD-${this.activeSalesOrder?.soNumber}-${Date.now()}`,
            loadUUID: this.generateUniqueId(),
            trailerNumber: data.trailerNumber,
            yardLocation: data.yardLocation || "Main Yard",
            loadDate: data.scheduledLoadDate,
            loadTime: "08:00",
            salesOrderId: this.activeSalesOrderId,
            numberOfPanels: this.generatedPanels.length,
            status: "Scheduled",
          };

          // Create load using ServiceStore
          const loadStore = this.rootStore.loadStore as ServiceStore;
          loadStore?.create?.(loadData);

          // Update panels with load information using ServiceStore
          const panelStore = this.rootStore.panelStore as ServiceStore;
          this.generatedPanels.forEach((panel: any) => {
            panelStore?.patch?.(panel.id as number, {
              loadId: loadData.id,
              loadNumber: loadData.loadNumber,
              loadUUID: loadData.loadUUID,
            });
          });

          // Simulate async delay
          await new Promise((resolve: any) => setTimeout(resolve, 1000));

          runInAction(() => {
            this.assignedLoadId = loadData.id;
            this.currentStep = 5;
          });

          this.rootStore.notificationStore?.addNotification?.({
            message: `Assigned ${this.generatedPanels.length} panels to load ${loadData.loadNumber}`,
            options: { variant: "success", autoHideduration: 4000 },
          });
        },
        { operation: "assignToDispatch", data }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  // Async Actions - Data Loading
  loadQuote = async (quoteId: number) => {
    this.isLoading = true;

    return this.errorHandler
      .withErrorHandling(
        "load",
        async () => {
          const quote = await (
            this.rootStore.quotesStore as ServiceStore
          )?.get?.(quoteId);
          if (!quote) {
            throw new Error("Quote not found");
          }

          runInAction(() => {
            this.activeQuoteId = quoteId;
          });

          // Load related panels if they exist
          await this.loadRelatedPanels();
        },
        { operation: "loadQuote", quoteId }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  loadSalesOrder = async (soId: number) => {
    this.isLoading = true;

    return this.errorHandler
      .withErrorHandling(
        "load",
        async () => {
          const salesOrder = await (
            this.rootStore.salesOrdersStore as ServiceStore
          )?.get?.(soId);
          if (!salesOrder) {
            throw new Error("Sales order not found");
          }

          runInAction(() => {
            this.activeSalesOrderId = soId;
          });

          // Load related panels if they exist
          await this.loadRelatedPanels();
        },
        { operation: "loadSalesOrder", soId }
      )
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  loadRelatedPanels = async () => {
    if (!this.activeSalesOrderId) return;

    try {
      // ServiceStores will automatically handle finding related panels
      // No need to explicitly load as they'll be available through computed properties
      if (this.generatedPanels.length > 0) {
        this.currentStep = Math.max(this.currentStep, 3);
      }
    } catch (err) {
      console.error("Failed to load related panels:", err);
    }
  };

  // Utility Methods
  generateUniqueId = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  // Disposal method for proper cleanup
  dispose = () => {
    if (this.errorHandler) {
      this.errorHandler.dispose();
    }
  };
}
